import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Email } from '@kula-ai/components';
import type {
  EditorProps,
  EmailCompositionValue,
  EmailProps,
} from '@kula-ai/components';
import type { SelectOption } from '@kula-ai/components';
import {
  applyTemplateMetadataOnChange,
  mapTemplateAttachmentsToEditorItems,
} from '../InterviewActivity/communication/helpers/templateApply';
import type {
  InterviewEditorAttachmentItem,
  InterviewVariablesByCategory,
} from '../InterviewActivity/communication/types';

function interviewFileToAttachmentItem(
  file: File
): InterviewEditorAttachmentItem {
  return {
    key: `${file.name}-${file.size}-${file.lastModified}`,
    name: file.name,
    size: file.size,
    mimeType: file.type,
    file,
  };
}

export type InterviewEmailEditorVariant = 'calendarInvite' | 'richEmail';

export type TemplatedEmailProps = Omit<
  EmailProps,
  'onChange' | 'templateOptions'
> & {
  templateOptions: SelectOption[];
  value: EmailCompositionValue;
  onChange: (_value: EmailCompositionValue) => void;
  editorVariant?: InterviewEmailEditorVariant;
  variables?: InterviewVariablesByCategory;
  attachments?: InterviewEditorAttachmentItem[];
  onAttachmentsChange?: (_items: InterviewEditorAttachmentItem[]) => void;
  /**
   * Bumped per submit click so the underlying `Email` re-fires its
   * in-editor auto-scroll on repeat submits with unchanged errors.
   * Mirrors the `EmailProps.submitAttempt` contract — declared here
   * explicitly so a stale `@kula-ai/components` types view (TS server
   * caching pre-1.10.60 EmailProps without this field) doesn't make
   * consumers fail to type-check.
   */
  submitAttempt?: number | string;
  /**
   * Host inline-image upload (e.g. POST `/user_image_uploads`). When
   * present, the editor uploads picked files and inserts the returned
   * URL; when absent, the underlying editor falls back to a base64 data
   * URL that bloats the saved `*_comm_config.body` payload. Pass it for
   * both calendar-invite and rich-email variants.
   */
  uploadInlineImage?: EditorProps['uploadInlineImage'];
};

export function TemplatedEmail({
  templateOptions,
  value,
  onChange,
  editorProps,
  editorVariant = 'calendarInvite',
  variables,
  attachments,
  onAttachmentsChange,
  uploadInlineImage,
  subjectVariableInputProps,
  ...rest
}: TemplatedEmailProps) {
  const prev = useRef(value);
  useEffect(() => {
    prev.current = value;
  }, [value]);

  const handleChange = useCallback(
    (next: EmailCompositionValue) => {
      const merged = applyTemplateMetadataOnChange(
        prev.current,
        next,
        templateOptions
      );
      prev.current = merged;
      onChange(merged);
    },
    [onChange, templateOptions]
  );

  // When template changes, seed its attachments into user-controlled state
  const prevTemplateId = useRef(value.template);
  useEffect(() => {
    if (value.template === prevTemplateId.current) return;
    prevTemplateId.current = value.template;

    if (!onAttachmentsChange) return;
    if (!value.template) {
      // Template cleared — remove template-sourced attachments (keep user files)
      const userOnly = (attachments ?? []).filter((a) => a.file);
      onAttachmentsChange(userOnly);
      return;
    }
    // Template selected — seed its attachments
    const opt = templateOptions.find((o) => o.value === value.template);
    const templateItems = mapTemplateAttachmentsToEditorItems(
      opt?.data?.attachments
    ) as InterviewEditorAttachmentItem[];
    if (templateItems.length) {
      // Keep existing user files, add template attachments
      const userFiles = (attachments ?? []).filter((a) => a.file);
      onAttachmentsChange([...templateItems, ...userFiles]);
    }
  }, [value.template, templateOptions, attachments, onAttachmentsChange]);

  const toolbarFeatures = useMemo(
    () =>
      editorVariant === 'calendarInvite'
        ? { image: false, attachment: false }
        : { image: true, attachment: true },
    [editorVariant]
  );

  const mergedEditorProps = useMemo(() => {
    const allAttachments =
      editorVariant === 'richEmail' && attachments?.length
        ? attachments
        : undefined;

    const attachmentHandlers =
      editorVariant === 'richEmail' && onAttachmentsChange
        ? {
            onAttachFiles: (files: File[]) => {
              const added = files.map(interviewFileToAttachmentItem);
              onAttachmentsChange([...(attachments ?? []), ...added]);
            },
            onRemoveAttachment: (key: string) => {
              onAttachmentsChange(
                (attachments ?? []).filter((a) => a.key !== key)
              );
            },
          }
        : {};

    // Forwarded to `Editor` for both variants. Calendar invites hide the
    // toolbar image button via `toolbarFeatures.image: false`, but the
    // node-view's in-place replace input and any future paste/drop hook
    // still need a host upload path so base64 doesn't end up on the wire.
    const inlineImageProp = uploadInlineImage ? { uploadInlineImage } : {};

    return {
      ...editorProps,
      variables: variables ?? editorProps?.variables,
      toolbarFeatures: {
        ...editorProps?.toolbarFeatures,
        ...toolbarFeatures,
      },
      ...(allAttachments ? { attachments: allAttachments } : {}),
      ...attachmentHandlers,
      ...inlineImageProp,
    };
  }, [
    attachments,
    editorProps,
    editorVariant,
    onAttachmentsChange,
    toolbarFeatures,
    uploadInlineImage,
    variables,
  ]);

  const mergedSubjectVariableInputProps = useMemo(
    () =>
      variables && Object.keys(variables).length > 0
        ? { ...subjectVariableInputProps, variables }
        : subjectVariableInputProps,
    [subjectVariableInputProps, variables]
  );

  return (
    <Email
      {...rest}
      editorProps={mergedEditorProps}
      subjectVariableInputProps={mergedSubjectVariableInputProps}
      templateOptions={templateOptions}
      value={value}
      onChange={handleChange}
    />
  );
}
